import React, {
    useState, useCallback, useRef, useEffect
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import FormValidator from 'utils/FormValidator';
import MobilePhoneInput from 'components/Inputs/MobilePhoneInput/MobilePhoneInput';
import Modal from 'components/Modal';
import {
    Box, Button, Text, Link, Flex
} from 'components/ui';
import ErrorList from 'components/ErrorList';
import { PHONE_VERIFICATION_ERROR_CODES } from 'constants/phoneVerification';
import InfoButton from 'components/InfoButton/InfoButton';
import OtpInputGroup from 'components/OtpInputGroup';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import linkTrackingError from 'analytics/bindings/pages/all/linkTrackingError';
import analyticsUtils from 'analytics/utils';

const PhoneVerificationModal = props => {
    const {
        isOpen,
        lastFourDigits,
        email,
        localization = {},
        onDismiss,
        onSwitchToEmail,
        displayInfoModal,
        sendPhoneOTP,
        onMFASuccess,
        checkOTP
    } = props;

    // Local state
    const [formattedPhone, setFormattedPhone] = useState('');
    const [rawPhone, setRawPhone] = useState('');
    const [errorMessages, setErrorMessages] = useState([]);
    const [isValidating, setIsValidating] = useState(false);
    const [sendDisabled, setSendDisabled] = useState(false);
    const [phoneValidated, setPhoneValidated] = useState(false);
    const [resentEnabled, setResentEnabled] = useState(false);
    const [timer, setTimer] = useState(0);

    // Refs
    const mobileInput = useRef();

    // Clear local state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setErrorMessages([]);
            setFormattedPhone('');
            setRawPhone('');
            setIsValidating(false);
            setPhoneValidated(false);
            setResentEnabled(false);
            setTimer(0);
        }
    }, [isOpen]);

    // Timer effect - only runs when OTP entry screen is shown (phoneValidated is true)
    useEffect(() => {
        if (!phoneValidated) {
            return;
        }

        // Initialize timer when OTP entry screen first shows or when resend is clicked
        setTimer(30);
        setResentEnabled(false);

        const interval = setInterval(() => {
            setTimer(prevTimer => {
                if (prevTimer <= 1) {
                    setResentEnabled(true);

                    return 0;
                }

                return prevTimer - 1;
            });
        }, 1000);

        // eslint-disable-next-line consistent-return
        return () => clearInterval(interval);
    }, [phoneValidated]);

    const clearError = () => setErrorMessages([]);

    const onShowInfo = () => {
        displayInfoModal({
            title: localization.infoTitle,
            message: localization.infoMessage,
            buttonText: localization.gotIt
        });
    };

    const handlePhoneChange = useCallback(
        phone => {
            setRawPhone(phone);
            const formatted = FormValidator.getFormattedPhoneNumber(phone);
            setFormattedPhone(formatted || phone);

            // Clear errors when user starts typing
            if (errorMessages.length > 0) {
                clearError();
            }
        },
        [errorMessages.length]
    );

    const validatePhone = useCallback(
        phoneValue => {
            const isValidLength = formattedPhone?.length === FormValidator.FIELD_LENGTHS.formattedPhone;

            if (FormValidator.isEmpty(phoneValue) || !isValidLength) {
                return localization.errorInvalidPhone;
            }

            return null;
        },
        [formattedPhone, localization.errorInvalidPhone]
    );

    const handleDismiss = useCallback(() => {
        if (onDismiss) {
            // ACS-2685 AC3: Fire anlytics after user clicks "Cancel" button
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION_LANDING}:*`,
                    actionInfo: anaConsts.LinkData.PHONE_VERIFICATION_CANCEL
                }
            });

            onDismiss();
        }
    }, [onDismiss]);

    const handleCheckOtp = useCallback(
        async otpCode => {
            try {
                const otpResponse = await checkOTP(otpCode);

                // OTP verified successfully - complete the login flow
                if (onMFASuccess) {
                    onMFASuccess(otpResponse);

                    // ACS-2686 AC4: Fire TrackAction after user successful code verification
                    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                        data: {
                            actionInfo: anaConsts.LinkData.PHONE_VERIFICATION_VERIFIED,
                            eventStrings: ['event3']
                        }
                    });
                }
            } catch (error) {
                let errorMessage;
                const errorCode = error.errorCode || error.code;

                switch (errorCode) {
                    case PHONE_VERIFICATION_ERROR_CODES.ERR_OTP_INVALID_CODE:
                        setErrorMessages([localization.errorInvalidOtp]);
                        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                            data: {
                                bindingMethods: linkTrackingError,
                                fieldErrors: [anaConsts.FIELD_ERRORS.BI_VERIFICATION],
                                errorMessages: [localization.errorInvalidOtp]
                            }
                        });

                        break;
                    case PHONE_VERIFICATION_ERROR_CODES.ERR_OTP_VERIFY_TOO_MANY_ATTEMPTS:
                        // AC3: Fire TrackState for OTP "Too Many Attempts" modal
                        processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                            data: {
                                ...analyticsUtils.getLastAsyncPageLoadData(),
                                pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.PHONE_OTP}:${anaConsts.PAGE_DETAIL.PHONE_OTP_TOO_MANY_ATTEMPTS}:*`,
                                pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                                pageDetail: anaConsts.PAGE_DETAIL.PHONE_OTP
                            }
                        });
                        displayInfoModal({
                            title: localization.tooManyAttempts,
                            message: localization.errorMaxAttemptOtp,
                            buttonText: localization.gotIt,
                            showCancelButton: false,
                            callback: onDismiss
                        });

                        break;
                    case PHONE_VERIFICATION_ERROR_CODES.ERR_OTP_VERIFY_INTERNAL_ERROR:
                        errorMessage = error.errorMessages?.[0] || error.message || localization.errorInternalServerOTP;
                        setErrorMessages([errorMessage]);
                        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                            data: {
                                bindingMethods: linkTrackingError,
                                fieldErrors: [anaConsts.FIELD_ERRORS.BI_VERIFICATION],
                                errorMessages: [errorMessage]
                            }
                        });

                        break;
                    case PHONE_VERIFICATION_ERROR_CODES.ERR_OTP_VERIFY_EXPIRED:
                        errorMessage = error.errorMessages?.[0] || error.message || localization.errorInternalServerOTP;
                        setErrorMessages([localization.expiredOTP]);

                        break;
                    default:
                        errorMessage = error.errorMessages?.[0] || error.message || localization.errorInternalServerOTP;
                        setErrorMessages([errorMessage]);
                        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                            data: {
                                bindingMethods: linkTrackingError,
                                fieldErrors: [anaConsts.FIELD_ERRORS.BI_VERIFICATION],
                                errorMessages: [errorMessage]
                            }
                        });

                        break;
                }
            }
        },
        [email, onMFASuccess, localization, onDismiss, onSwitchToEmail]
    );

    const handleVerify = useCallback(async () => {
        // Validate phone number format
        const validationError = validatePhone(rawPhone);

        if (validationError) {
            setErrorMessages([validationError]);
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    bindingMethods: linkTrackingError,
                    fieldErrors: ['sign in:bi verification'],
                    errorMessages: [validationError]
                }
            });

            return;
        }

        // Clear previous errors
        clearError();
        setIsValidating(true);

        try {
            // ACS-2685 AC2: Fire anlytics after user clicks "Send" button
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION_LANDING}:*`,
                    actionInfo: anaConsts.LinkData.PHONE_VERIFICATION_SEND
                }
            });

            // Validate phone number via action
            await sendPhoneOTP(rawPhone);

            // OTP sent successfully
            setPhoneValidated(true);
        } catch (error) {
            // Map error codes to user-friendly messages
            let errorMessage;
            const errorCode = error.errorCode || error.code;

            switch (errorCode) {
                case PHONE_VERIFICATION_ERROR_CODES.INVALID_PHONE: {
                    setErrorMessages([localization.errorPhoneMismatch]);
                    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                        data: {
                            bindingMethods: linkTrackingError,
                            fieldErrors: ['sign in:bi verification'],
                            errorMessages: [localization.errorPhoneMismatch]
                        }
                    });

                    break;
                }
                case PHONE_VERIFICATION_ERROR_CODES.TOO_MANY_ATTEMPTS: {
                    setSendDisabled(true);
                    // AC2: Fire TrackState for phone verification "Too Many Attempts" modal
                    const pageName = `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION_TOO_MANY_ATTEMPTS}:*`;

                    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                        data: {
                            ...analyticsUtils.getLastAsyncPageLoadData(),
                            pageName,
                            pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                            pageDetail: anaConsts.PAGE_DETAIL.PHONE_VERIFICATION
                        }
                    });
                    displayInfoModal({
                        title: localization.tooManyAttempts,
                        message: localization.errorMaxAttempts,
                        buttonText: localization.emailVerification,
                        showCancelButtonLeft: true,
                        cancelText: localization.cancelButton,
                        cancelCallback: handleDismiss,
                        callback: () => onSwitchToEmail({ previousPageName: pageName })
                    });

                    break;
                }
                case PHONE_VERIFICATION_ERROR_CODES.INTERNAL_ERROR: {
                    // AC4: Fire TrackState for SMS error modal
                    const pageName = `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION_ERROR}:*`;
                    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                        data: {
                            ...analyticsUtils.getLastAsyncPageLoadData(),
                            pageName,
                            pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                            pageDetail: anaConsts.PAGE_DETAIL.PHONE_VERIFICATION,
                            prop28: anaConsts.FIELD_ERRORS.BI_VERIFICATION,
                            prop48: localization.errorNetwork
                        }
                    });
                    displayInfoModal({
                        title: localization.accessibilityError,
                        message: localization.errorNetwork,
                        buttonText: localization.emailVerification,
                        showCancelButton: true,
                        cancelCallback: handleDismiss,
                        callback: () => onSwitchToEmail({ previousPageName: pageName })
                    });

                    break;
                }
                default:
                    errorMessage = error.errorMessages?.[0] || error.message || localization.errorInternalServer;
                    setErrorMessages([errorMessage]);

                    break;
            }
        } finally {
            setIsValidating(false);
        }
    }, [rawPhone, email, validatePhone, handleDismiss, localization]);

    const resentOtp = useCallback(() => {
        handleVerify();

        // ACS-2686 AC2: Fire anlytics after user clicks "Send" button
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION_VERIFICATION}:*`,
                actionInfo: anaConsts.LinkData.PHONE_VERIFICATION_RESEND
            }
        });
    }, [handleVerify]);

    // ACS-2685 AC1: Fire when user lands on the Phone Verification landing screen
    useEffect(() => {
        if (isOpen && !phoneValidated) {
            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    ...analyticsUtils.getLastAsyncPageLoadData(),
                    pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION_LANDING}:*`,
                    pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                    pageDetail: anaConsts.PAGE_DETAIL.PHONE_VERIFICATION
                }
            });
        }
    }, [isOpen, phoneValidated]);

    // ACS-2686 AC1: Fire when user lands on the Phone Verification OTP Entry screen
    useEffect(() => {
        if (isOpen && phoneValidated) {
            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    ...analyticsUtils.getLastAsyncPageLoadData(),
                    pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION_VERIFICATION}:*`,
                    pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                    pageDetail: anaConsts.PAGE_DETAIL.PHONE_VERIFICATION,
                    previousPageName: analyticsUtils.getLastAsyncPageLoadData().pageName
                }
            });
        }
    }, [isOpen, phoneValidated]);

    const handleOnSwitchToEmail = useCallback(
        () =>
            onSwitchToEmail({
                previousPageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.PHONE_VERIFICATION}:${phoneValidated ? anaConsts.PAGE_DETAIL.PHONE_VERIFICATION_VERIFICATION : anaConsts.PAGE_DETAIL.PHONE_VERIFICATION_LANDING}:*`
            }),
        [phoneValidated, onSwitchToEmail]
    );

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={handleDismiss}
            width={phoneValidated ? 0 : 1}
            dataAt={Sephora.debug.dataAt('phone_verification_modal')}
        >
            <Modal.Header>
                <Modal.Title>{localization.modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body lineHeight='tight'>
                {!phoneValidated ? (
                    <>
                        <Text
                            is='h2'
                            fontWeight='bold'
                            fontSize='md'
                            marginBottom={2}
                        >
                            {localization.heading}
                        </Text>

                        <Text
                            is='p'
                            marginBottom={3}
                        >
                            {localization.subheading}
                            <InfoButton
                                marginLeft={1}
                                onClick={onShowInfo}
                                verticalAlign='middle'
                                size={16}
                            />
                        </Text>

                        {lastFourDigits && (
                            <Text
                                is='p'
                                marginBottom={3}
                            >
                                {localization.phoneOnFilePrefix}{' '}
                                <Text
                                    is='span'
                                    fontWeight='bold'
                                >
                                    {lastFourDigits}
                                </Text>{' '}
                                {localization.phoneOnFileSuffix}
                            </Text>
                        )}

                        <MobilePhoneInput
                            ref={mobileInput}
                            name='mobilePhone'
                            required={true}
                            value={formattedPhone}
                            autoCorrect='off'
                            label={localization.enterPhoneNumber}
                            aria-label={localization.accessibilityPhoneInput}
                            maxLength={FormValidator.FIELD_LENGTHS.formattedPhone}
                            onChange={handlePhoneChange}
                            validate={validatePhone}
                            dataAtError={Sephora.debug.dataAt('error_msg')}
                            invalid={errorMessages.length > 0}
                        />

                        {errorMessages.length > 0 && <ErrorList errorMessages={errorMessages} />}

                        <Text
                            is='p'
                            fontSize='xs'
                            color='gray'
                            marginTop={3}
                            marginBottom={3}
                        >
                            {localization.privacyNote}
                            <Link
                                color='blue'
                                fontWeight='bold'
                                href='https://www.sephora.com/beauty/terms-of-use'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {localization.termsOfUse}
                            </Link>
                            {localization.privacyNoteEnd}
                            <Link
                                color='blue'
                                fontWeight='bold'
                                href='https://www.sephora.com/beauty/privacy-policy'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {localization.privacyPolicy}
                            </Link>
                            .
                        </Text>

                        <Flex gap={2}>
                            <Button
                                variant='secondary'
                                onClick={handleDismiss}
                                style={{ flex: 1 }}
                                dataAt={Sephora.debug.dataAt('cancel_button')}
                            >
                                {localization.cancelButton}
                            </Button>

                            <Button
                                variant='primary'
                                onClick={handleVerify}
                                disabled={isValidating || !rawPhone || sendDisabled}
                                style={{ flex: 1 }}
                                aria-busy={isValidating}
                                dataAt={Sephora.debug.dataAt('send_button')}
                            >
                                {isValidating ? localization.sending : localization.sendButton}
                            </Button>
                        </Flex>
                    </>
                ) : (
                    <>
                        <Text
                            is='h2'
                            fontWeight='bold'
                            fontSize='md'
                            marginBottom={2}
                        >
                            {localization.otpHeading}
                        </Text>
                        {lastFourDigits && (
                            <Text
                                is='p'
                                marginBottom={3}
                            >
                                {localization.otpSubheading}{' '}
                                <Text
                                    is='span'
                                    fontWeight='bold'
                                >
                                    ••••••{lastFourDigits}
                                </Text>{' '}
                                {localization.otpSubheadingCont}
                            </Text>
                        )}
                        {errorMessages.length > 0 && <ErrorList errorMessages={errorMessages} />}
                        <Text
                            is='p'
                            marginTop={3}
                        >
                            <OtpInputGroup
                                onComplete={handleCheckOtp}
                                error={errorMessages.length > 0}
                                clearError={clearError}
                            />

                            <Box marginTop={2}>
                                {resentEnabled ? (
                                    <>
                                        {localization.needAnotherSms}
                                        <Link
                                            color='blue'
                                            onClick={resentOtp}
                                            style={{ marginLeft: '4px', cursor: 'pointer' }}
                                            underline
                                        >
                                            {localization.resendButton}
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        {localization.didntGetSms}{' '}
                                        <Text
                                            is='span'
                                            fontWeight='bold'
                                        >
                                            {timer}{' '}
                                        </Text>
                                        {localization.seconds}
                                    </>
                                )}
                            </Box>
                        </Text>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer hasBorder={true}>
                <Text
                    is='p'
                    marginBottom={4}
                >
                    {localization.noAccessTextPrefix}
                    <Text
                        is='span'
                        fontWeight='bold'
                        children={email}
                    />
                </Text>
                <Button
                    variant='secondary'
                    onClick={handleOnSwitchToEmail}
                    paddingX='32px'
                    size='md'
                    dataAt={Sephora.debug.dataAt('send_to_email_button')}
                >
                    {localization.sendToEmailButton}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default wrapFunctionalComponent(PhoneVerificationModal, 'PhoneVerificationModal');
